import { getCookie } from '@/lib/cookie';
import { showModal } from '@/lib/modal';
import type { EventOf } from '@/lib/utils';
import { $1, $, $e, $hidden, csrfToken, on } from '@/lib/utils';

interface CurrentCartsItems {
  readonly items: number;
}

getCart();
on('change', 'form[data-cart-recalc] select[name^=quantity]', (ev: EventOf<HTMLSelectElement>) => {
  recalcCart(ev.currentTarget.form!);
});
on('click', 'form[data-cart-recalc] button[data-remove-item]', handleRemoveItem);
on('click', 'form[data-cart-recalc] button[data-remove-coupon]', handleRemoveCoupon);
on('submit', 'form.cart-add-item', handleCartAddItem);
on('click', '.add-coupon', (ev: EventOf<HTMLButtonElement>) => handleAddCoupon(ev.currentTarget.form!));
on('keydown', '.add-coupon-code', (ev: EventOf<HTMLInputElement, KeyboardEvent>) => {
  if (ev.key === 'Enter') {
    ev.preventDefault();
    handleAddCoupon(ev.currentTarget.form!);
  }
});

on('change', '[data-change]', (ev: EventOf<HTMLSelectElement>) => {
  const elem = ev.currentTarget;
  switch (elem.dataset.change) {
    case 'refresh':
      location.href = elem.value;
      break;
    case 'submit':
      elem.form?.submit();
      break;
  }
});

async function getCart(force = false) {
  let items = parseInt(getCookie('__Host-cart') || '', 10);
  if (force || Number.isNaN(items) || items < 0) {
    const res = await fetch('/ajax/current_carts_items');
    const json = (await res.json()) as CurrentCartsItems;
    items = json.items;
  }

  const elem = $('cart-quantities') as HTMLOutputElement;
  if (items > 0) Object.assign(elem, { hidden: false, value: items });

  const elem2 = $('cart-quantities-pc') as HTMLOutputElement;
  if (items > 0) Object.assign(elem2, { hidden: false, value: items });
}

async function recalcCart(form: HTMLFormElement) {
  try {
    const res = await fetch(form.dataset.cartRecalc!, {
      method: 'PATCH',
      mode: 'same-origin',
      credentials: 'include',
      body: new FormData(form),
      headers: { 'X-CSRF-Token': csrfToken() },
      redirect: 'manual',
    });
    if (res.type === 'opaqueredirect' || res.status === 204) {
      location.reload();
    } else if (!res.ok) {
      if (res.headers.get('Content-Type')?.includes('application/json')) {
        const json = await res.json();
        alert(json.error || json);
        if (json.reload) location.reload();
      } else {
        alert('買い物かごの再計算に失敗しました。再読込します。');
        location.reload();
      }
    } else {
      form.querySelector('.cart-form-intern')!.innerHTML = await res.text();
    }
  } catch (err) {
    alert(err);
  } finally {
    const couponCode = form.querySelector<HTMLInputElement>('.add-coupon-code');
    if (couponCode) {
      couponCode.value = '';
      setTimeout(() => couponCode.classList.remove('is-modified'), 1);
    }
  }
}

function handleRemoveItem(ev: EventOf<HTMLButtonElement>) {
  const e = ev.currentTarget;
  const form = e.form!;
  if (form.dataset.cartDisallowEmpty && form.querySelectorAll('select[name^=quantity]').length <= 1) {
    alert('買い物かごの商品をすべて削除することはできません。代わりに注文を取り消してください。');
    return;
  }
  const quantity = e.closest('tr')!.querySelector<HTMLSelectElement>('select[name^=quantity]');
  if (quantity && confirm(`『${e.dataset.removeItem}』を買い物かごから削除します。`)) {
    quantity.replaceChildren($e('option', { value: '0' }, '0'));
    quantity.value = '0';
    recalcCart(form);
  }
}

function handleAddCoupon(form: HTMLFormElement) {
  const couponCode = form.elements.namedItem('add_coupon_code');
  if (couponCode instanceof HTMLInputElement && couponCode.value) recalcCart(form);
}

function handleRemoveCoupon(ev: EventOf<HTMLButtonElement>) {
  const e = ev.currentTarget;
  const form = e.form!;
  if (confirm(`クーポン『${e.dataset.couponName}』を買い物かごから削除します。`)) {
    const hidden = $hidden('remove_coupon_code', e.dataset.removeCoupon);
    form.append(hidden);
    recalcCart(form).finally(() => hidden.remove());
  }
}

async function handleCartAddItem(ev: EventOf<HTMLFormElement, SubmitEvent>) {
  const form = ev.currentTarget;
  if (!form.checkValidity()) return;

  ev.preventDefault();

  const submitter = ev.submitter as HTMLButtonElement;
  const submitIcon = submitter.querySelector('i')!;
  submitIcon.classList.remove('fa-cart-plus');
  submitIcon.classList.add('fa-spinner');
  submitIcon.classList.add('fa-spin');
  submitter.disabled = true;
  const reset = () => {
    submitIcon.classList.remove('fa-spin');
    submitIcon.classList.remove('fa-spinner');
    submitIcon.classList.add('fa-cart-plus');
    submitter.disabled = false;
    form.classList.remove('is-modified');
    form.querySelectorAll('.is-modified').forEach((el) => el.classList.remove('is-modified'));
  };

  try {
    const res = await fetch(form.action, {
      method: 'POST',
      mode: 'same-origin',
      credentials: 'include',
      body: new FormData(form),
      headers: { 'X-CSRF-Token': csrfToken() },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (json.error) throw new Error(json.error);

    getCart(true).catch(console.error);
    if (!form.classList.contains('cart-add-item-inline')) {
      $1('#item-added .result')!.innerHTML = json.result;
      showModal($('item-added')!);
      await new Promise((resolve) => setTimeout(resolve, 200));
    } else {
      form.querySelector('.inline-item-added')?.classList.remove('invisible');
    }
  } catch (err) {
    alert(err);
  } finally {
    reset();
  }
}
